// src/components/WeekView.jsx

import React, { useMemo } from 'react';
import { TableContainer, Table, Thead, Tbody, Tr, Th, Td, Button, Tag, Tooltip, Box, VStack } from '@chakra-ui/react';
import { useAuthCliente } from '../context/AuthClienteContext';
import { format, addDays, startOfWeek, isSameDay, isBefore, startOfToday, startOfDay, endOfDay } from 'date-fns';
import { es } from 'date-fns/locale/es';

export default function WeekView({ settings, bookings, globalEvents, selectedDate, onSlotSelect }) {
  const { currentUser } = useAuthCliente();

  const weekDays = useMemo(() => Array.from({ length: 7 }).map((_, i) => 
    addDays(startOfWeek(selectedDate, { weekStartsOn: 1 }), i)
  ), [selectedDate]);

  const timeSlots = useMemo(() => {
    if (!settings) return [];
    const slots = [];
    const { openingHour = 7, closingHour = 22 } = settings;
    for (let i = openingHour; i < closingHour; i++) {
        slots.push({ start: `${String(i).padStart(2, '0')}:00`, end: `${String(i + 1).padStart(2, '0')}:00` });
    }
    return slots;
  }, [settings]);
  
  const currentHour = new Date().getHours();

  return (
    <TableContainer borderWidth="1px" borderRadius="lg" bg="white" mt={6}>
      <Table variant="simple" size="sm">
        <Thead bg="gray.50">
          <Tr>
            <Th w="120px">Hora</Th>
            {weekDays.map(day => (
              <Th key={day.toISOString()} textAlign="center" textTransform="capitalize">
                <Box fontSize="sm">{format(day, 'EEE', { locale: es })}</Box>
                <Box fontSize="lg" fontWeight="bold">{format(day, 'd')}</Box>
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {timeSlots.map(time => (
            <Tr key={time.start}>
              <Td fontWeight="bold">{time.start}</Td>
              {weekDays.map(day => {
                const dayOfWeek = day.getDay();
                const formattedDate = format(day, 'yyyy-MM-dd');
                const isPastDate = isBefore(day, startOfToday());
                const isToday = isSameDay(day, new Date());
                
                return (
                  <Td key={day.toISOString()} p={1} verticalAlign="top">
                    <VStack spacing={1} align="stretch">
                      {settings.resources.map(resource => {
                        const { schedule = {}, specialEvents = [] } = resource;
                        const slotStartTime = new Date(`1970-01-01T${time.start}`);
                        const resourceStartTime = new Date(`1970-01-01T${schedule.startTime || '23:59'}`);
                        const resourceEndTime = new Date(`1970-01-01T${schedule.endTime || '00:00'}`);
                        
                        if (!((schedule.operatingDays || []).includes(dayOfWeek) && slotStartTime >= resourceStartTime && slotStartTime < resourceEndTime)) {
                          return <Box h="32px" key={resource.id} />;
                        }
                        
                        const slotHour = parseInt(time.start, 10);
                        const shouldBeDisabled = isPastDate || (isToday && slotHour < currentHour);

                        const specialEvent = (specialEvents || []).find(e => {
                          const eventStart = startOfDay(new Date(e.startDate));
                          const eventEnd = endOfDay(new Date(e.endDate));
                          return startOfDay(day) >= eventStart && startOfDay(day) <= eventEnd;
                        });
                        if (specialEvent) {
                          if (specialEvent.type === 'vacation' || (specialEvent.startTime <= time.start && specialEvent.endTime > time.start)) {
                            return <Tooltip label={specialEvent.title}><Tag key={resource.id} w="full" h="32px" justifyContent="center" bg={specialEvent.color || 'orange.400'} color="white">EVENTO</Tag></Tooltip>;
                          }
                        }
                        
                        const globalEvent = (globalEvents || []).find(e => 
                          (e.assignedResourceIds || []).includes(resource.id) && 
                          (e.days || []).includes(dayOfWeek) && 
                          time.start >= e.startTime && 
                          time.start < e.endTime
                        );
                        if (globalEvent) {
                          return <Tooltip label={globalEvent.title}><Tag key={resource.id} w="full" h="32px" justifyContent="center" colorScheme="purple">CLÍNICA</Tag></Tooltip>;
                        }

                        const booking = bookings.find(b => b.date === formattedDate && b.startTime === time.start && b.resourceId === resource.id);
                        if (booking) {
                          if (currentUser && booking.userId === currentUser.uid) {
                            return <Tag key={resource.id} w="full" h="32px" justifyContent="center" colorScheme="blue">Reservado por ti</Tag>;
                          }
                          return <Tag key={resource.id} w="full" h="32px" justifyContent="center" colorScheme="red">Reservado</Tag>;
                        }
                        
                        return (
                          <Button 
                            key={resource.id} 
                            size="xs" 
                            w="full"
                            h="32px"
                            variant="outline" 
                            colorScheme="green"
                            onClick={() => onSlotSelect(resource, time, day)}
                            fontWeight="normal"
                            isDisabled={shouldBeDisabled}
                          >
                            {resource.name}
                          </Button>
                        );
                      })}
                    </VStack>
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}
